/**
 * Competitors, grouped by how directly they compete. One row each: who they
 * are, why Mira thinks they compete (from their own homepage), and which of
 * their pages to monitor.
 */
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { Icon } from "@/components/ui/icon";
import { cn } from "@/lib/utils";
import type { KycCompetitor } from "@/lib/api";
import { Favicon } from "./BenchmarkTable";
import { RELATIONSHIP } from "./metrics";

const PAGE_ICONS: Record<string, string> = {
  homepage: "home-01", pricing: "tag-01", features: "flash", changelog: "file-01",
  blog: "file-star", docs: "book-open-01", careers: "user-group", about: "information-circle",
};

const GROUP_HINT: Record<"high" | "medium" | "low", string> = {
  high: "Same job, same customer — a buyer would shortlist both",
  medium: "Overlapping job or customer, different focus",
  low: "Solves the problem another way",
};

interface Props {
  competitors: KycCompetitor[];
  selected: Set<string>;
  pages: Record<string, Set<string>>;
  removingId: string | null;
  onToggle: (domain: string) => void;
  onSetGroup: (domains: string[], on: boolean) => void;
  onTogglePage: (domain: string, path: string) => void;
  onRemove: (competitor: KycCompetitor) => void;
}

export function CompetitorList({ competitors, selected, pages, removingId, onToggle, onSetGroup, onTogglePage, onRemove }: Props) {
  const columns: DataTableColumn<KycCompetitor>[] = [
    {
      id: "competitor",
      header: "Competitor",
      width: "14rem",
      cell: (c) => {
        const on = selected.has(c.domain);
        return (
          <div className="flex min-w-0 items-center gap-3">
            <Checkbox checked={on} label={`Monitor ${c.name}`} onChange={() => onToggle(c.domain)} />
            <Favicon domain={c.domain} name={c.name} />
            <div className={cn("min-w-0", !on && "opacity-60")}>
              <div className="flex items-center gap-1.5">
                <span className="truncate font-sans text-xs font-semibold">{c.name}</span>
                {c.source === "manual" && (
                  <span className="shrink-0 rounded border border-border px-1 py-px font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                    Yours
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      id: "website",
      header: "Website",
      width: "13rem",
      cell: (c) => (
        <a
          href={`https://${c.domain}`}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            "inline-flex max-w-full items-center gap-1 font-mono text-[11px] text-muted-foreground transition-colors hover:text-foreground",
            !selected.has(c.domain) && "opacity-60",
          )}
        >
          <span className="truncate">{c.domain}</span>
          <Icon name="arrow-up-right-01" size={11} className="shrink-0" />
        </a>
      ),
    },
    {
      id: "why",
      header: "Why they compete",
      cell: (c) => {
        const why = c.source === "manual" ? null : c.reason;
        return (
          <p className={cn(
            "line-clamp-2 font-sans text-xs leading-relaxed",
            why ? "text-foreground/80" : "text-muted-foreground",
            !selected.has(c.domain) && "opacity-60",
          )}>
            {why ?? (c.description || "No description on their homepage.")}
          </p>
        );
      },
    },
    {
      id: "pages",
      header: "Pages to monitor",
      width: "19rem",
      cell: (c) => {
        const on = selected.has(c.domain);
        return (
          <div className="flex flex-wrap gap-1.5">
            {c.suggestedPages.map((page) => {
              const active = on && pages[c.domain]?.has(page.path);
              return (
                <button
                  key={page.path}
                  type="button"
                  onClick={() => on && onTogglePage(c.domain, page.path)}
                  disabled={!on}
                  title={`${c.domain}${page.path}`}
                  className={cn(
                    "flex items-center gap-1 rounded-md border px-2 py-1 font-mono text-[11px] transition-colors",
                    active ? "border-foreground bg-foreground text-background" : "border-border text-muted-foreground",
                    on && !active && "hover:border-muted-foreground hover:text-foreground",
                    !on && "opacity-40",
                  )}
                >
                  <Icon name={PAGE_ICONS[page.label] ?? "link-01"} size={10} />
                  {page.label}
                </button>
              );
            })}
          </div>
        );
      },
    },
    {
      id: "actions",
      header: <span className="sr-only">Actions</span>,
      width: "3.25rem",
      align: "right",
      cell: (c) => (
        <button
          type="button"
          onClick={() => onRemove(c)}
          disabled={removingId === c.id}
          title="Not a competitor — remove and don't suggest again"
          aria-label={`Remove ${c.name}`}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground/60 transition-all hover:bg-foreground/5 hover:text-foreground focus-visible:opacity-100 disabled:opacity-40 md:opacity-0 md:group-hover:opacity-100"
        >
          <Icon name={removingId === c.id ? "refresh" : "delete-02"} size={13} className={removingId === c.id ? "animate-spin" : undefined} />
        </button>
      ),
    },
  ];

  const groups = (["high", "medium", "low"] as const).map((level) => {
    const rows = competitors.filter((c) => c.similarity === level);
    const domains = rows.map((c) => c.domain);
    const allOn = domains.length > 0 && domains.every((d) => selected.has(d));
    const rel = RELATIONSHIP[level];
    return {
      id: level,
      rows,
      header: (
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", rel.dot)} />
            <span className="font-mono text-[11px] uppercase tracking-widest">{rel.label}</span>
            <span className="font-mono text-[11px] text-muted-foreground">{rows.length}</span>
            <span className="hidden truncate font-sans text-[11px] text-muted-foreground md:inline">· {GROUP_HINT[level]}</span>
          </div>
          <button
            type="button"
            onClick={() => onSetGroup(domains, !allOn)}
            className="shrink-0 font-mono text-[11px] uppercase tracking-widest text-muted-foreground transition-colors hover:text-foreground"
          >
            {allOn ? "Deselect all" : "Select all"}
          </button>
        </div>
      ),
    };
  });

  return (
    <DataTable
      columns={columns}
      groups={groups}
      rowKey={(c) => c.id}
      rowClassName={(c) => (selected.has(c.domain) ? undefined : "bg-background/40")}
      minWidth={1040}
      verticalDividers
      empty="No competitors yet. Add one you know above, or re-discover."
    />
  );
}

function Checkbox({ checked, label, onChange }: { checked: boolean; label: string; onChange: () => void }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={label}
      onClick={onChange}
      className={cn(
        "flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border transition-colors",
        checked ? "border-foreground bg-foreground text-background" : "border-border hover:border-muted-foreground",
      )}
    >
      {checked && <Icon name="tick-01" size={10} />}
    </button>
  );
}
